import React, { useMemo, useState } from 'react'
import {
  CAlert,
  CBadge,
  CButton,
  CCol,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormTextarea,
  CInputGroup,
  CInputGroupText,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CRow,
} from '@coreui/react-pro'
import ToastComponent from 'src/components/common/TaostComponent.js'
import GameActivationService from 'src/service/GameActivationService'
import {
  COMPETITION_CATEGORIES,
  CLUB_SERVICE_PACKAGES,
  CLUB_TEAM_BANDS,
  LEAGUE_TEAM_BANDS,
  DURATION_OPTIONS,
  MATCH_CONTEST_PACKAGE,
  getGameType,
  getClubBasePrice,
  getLeagueBasePrice,
  computePricing,
  getCategoryLabel,
} from 'src/views/pages/register/startNewGame/gameSetupConfig'

// Mark Paid dialog. The super admin can correct the plan the game admin picked at
// signup (same options and prices as the "Start New Game" wizard) before marking it
// paid. If nothing in the plan is changed, the original row is left as-is.
// With mode="updatePlan" the same form edits the plan/amount of an already paid game.

const GAME_TYPE_LABELS = {
  1: 'Season Game',
  2: 'Tournament (Leagues)',
  3: 'Tournament (Indoor Leagues)',
  4: 'Match Contest',
}

const formatAmount = (amount) => `$${Number(amount || 0).toFixed(2)}`

const bandsFor = (category) =>
  category === 'club_competition' ? CLUB_TEAM_BANDS : LEAGUE_TEAM_BANDS

// Builds the starting form from the pricing row, falling back to game_type for
// games created before competition_category was recorded.
const planFromItem = (item) => {
  const category =
    item.competition_category ||
    (item.game_type === 1 ? 'club_competition' : item.game_type === 4 ? 'match_contest' : '')
  const band = bandsFor(category).find(
    // eslint-disable-next-line eqeqeq
    (b) => b.min == item.min_teams && b.max == item.max_teams,
  )
  return {
    competition_category: category,
    cricket_format: item.cricket_format || (item.game_type === 3 ? 'indoor' : 'outdoor'),
    service_package: category === 'club_competition' ? item.service_package || '' : '',
    team_band: band?.value || '',
    duration: item.competition_duration || 'season',
  }
}

const MarkPaidModal = ({ item, mode = 'markPaid', onClose, onSaved }) => {
  const isUpdate = mode === 'updatePlan'
  const original = useMemo(() => planFromItem(item), [item])
  const [plan, setPlan] = useState(original)
  const [amountOverride, setAmountOverride] = useState(null)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  const isClub = plan.competition_category === 'club_competition'
  const isMatchContest = plan.competition_category === 'match_contest'

  // Match Contest isn't offered at signup any more, but older games may still be on it.
  const categoryOptions = isMatchContest
    ? [...COMPETITION_CATEGORIES, { value: 'match_contest', label: 'Match Contest' }]
    : COMPETITION_CATEGORIES

  const teamBands = bandsFor(plan.competition_category)
  const selectedBand = teamBands.find((b) => b.value === plan.team_band)

  const basePrice = isClub
    ? getClubBasePrice(plan.service_package, plan.team_band)
    : isMatchContest
    ? MATCH_CONTEST_PACKAGE.basePrice
    : getLeagueBasePrice(plan.competition_category, plan.team_band)
  const pricing = computePricing(basePrice, plan.duration, isClub)

  const planChanged = Object.keys(original).some((key) => original[key] !== plan[key])
  const planComplete =
    !!plan.competition_category &&
    !!plan.duration &&
    (isMatchContest || !!plan.team_band) &&
    (!isClub || !!plan.service_package) &&
    (isClub || !!plan.cricket_format)

  // Amount follows the plan price until the super admin types their own.
  const amount = amountOverride ?? (planChanged ? pricing.finalPrice ?? '' : item.amount ?? '')
  const amountChanged = amount !== '' && Number(amount) !== Number(item.amount)

  const newGameType = getGameType(plan.competition_category, plan.cricket_format)
  const gameTypeChanges = planChanged && newGameType && newGameType !== item.game_type

  const packageLabel = isClub
    ? CLUB_SERVICE_PACKAGES.find((p) => p.value === plan.service_package)?.label
    : isMatchContest
    ? 'Match Contest Package'
    : 'Season Package'
  const productName = [
    getCategoryLabel(plan.competition_category) || (isMatchContest ? 'Match Contest' : ''),
    packageLabel,
    isMatchContest
      ? `${MATCH_CONTEST_PACKAGE.contestAllowance} contests allowance`
      : selectedBand?.label,
    DURATION_OPTIONS.find((d) => d.value === plan.duration)?.label,
  ]
    .filter(Boolean)
    .join(' - ')

  const setField = (field, value) => {
    setPlan((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'competition_category') {
        // Same reset rules as the signup wizard: bands and packages differ per category.
        const wasClub = prev.competition_category === 'club_competition'
        const nowClub = value === 'club_competition'
        if (wasClub !== nowClub) next.team_band = ''
        next.service_package = nowClub ? prev.service_package || 'platform_licence' : ''
        if (nowClub) next.cricket_format = 'outdoor'
      }
      return next
    })
  }

  const resetPlan = () => {
    setPlan(original)
    setAmountOverride(null)
  }

  const handleSave = () => {
    if (planChanged && !planComplete) {
      ToastComponent(
        isUpdate
          ? 'Please complete the plan before saving.'
          : 'Please complete the plan before marking it as paid.',
        'error',
      )
      return
    }
    if (amount === '' || Number(amount) < 0) {
      ToastComponent('Please enter a valid amount.', 'error')
      return
    }

    const payload = isUpdate ? { id: item.id, amount } : { id: item.id, amount, note }
    if (planChanged) {
      payload.plan = {
        competition_category: plan.competition_category,
        cricket_format: isClub ? 'outdoor' : plan.cricket_format,
        service_package: plan.service_package,
        min_teams: selectedBand?.min ?? null,
        max_teams: selectedBand?.max ?? null,
        competition_duration: plan.duration,
        product_name: productName,
      }
    }

    setSaving(true)
    const request = isUpdate ? GameActivationService.updatePlan : GameActivationService.markPaid
    request(payload)
      .then((res) => {
        if (res.status === 200) {
          ToastComponent(res.message, 'success')
          onSaved()
        } else {
          ToastComponent(res.message, 'error')
          if (res.status === 409) onSaved()
        }
      })
      .catch((err) =>
        ToastComponent(err?.response?.data?.message || 'Something went wrong', 'error'),
      )
      .finally(() => setSaving(false))
  }

  return (
    <CModal
      visible
      onClose={() => !saving && onClose()}
      backdrop="static"
      alignment="center"
      size="lg"
    >
      <CModalHeader>
        <strong>{isUpdate ? 'Update Plan' : 'Mark Game as Paid'}</strong>
      </CModalHeader>
      <CModalBody>
        <div className="border rounded p-3 mb-3 bg-body-tertiary">
          <div className="fw-semibold">{item.game_name}</div>
          <div className="small text-medium-emphasis">
            #{item.game_id} · {item.admin_name} ({item.admin_email})
          </div>
          <div className="small mt-2">
            <span className="text-medium-emphasis">
              {isUpdate ? 'Current plan: ' : 'Admin picked: '}
            </span>
            {item.product_name || '-'} · <strong>{formatAmount(item.amount)}</strong>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>Plan</strong>
          {planChanged ? (
            <div className="d-flex align-items-center gap-2">
              <CBadge color="warning">Changed</CBadge>
              <CButton color="link" size="sm" className="p-0" onClick={resetPlan}>
                {isUpdate ? 'Reset to current plan' : <>Reset to admin&apos;s choice</>}
              </CButton>
            </div>
          ) : (
            <span className="small text-medium-emphasis">
              {isUpdate
                ? 'Change the plan or amount below'
                : 'Change anything below if the admin picked the wrong plan'}
            </span>
          )}
        </div>

        <CRow className="g-3">
          <CCol md={6}>
            <CFormLabel>Competition type</CFormLabel>
            <CFormSelect
              value={plan.competition_category}
              onChange={(e) => setField('competition_category', e.target.value)}
            >
              <option value="">Select competition type</option>
              {categoryOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </CFormSelect>
          </CCol>

          {isClub ? (
            <CCol md={6}>
              <CFormLabel>Package</CFormLabel>
              <CFormSelect
                value={plan.service_package}
                onChange={(e) => setField('service_package', e.target.value)}
              >
                <option value="">Select package</option>
                {CLUB_SERVICE_PACKAGES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label} – {p.description}
                  </option>
                ))}
              </CFormSelect>
            </CCol>
          ) : (
            <CCol md={6}>
              <CFormLabel>Format</CFormLabel>
              <CFormSelect
                value={plan.cricket_format}
                onChange={(e) => setField('cricket_format', e.target.value)}
                disabled={!plan.competition_category}
              >
                <option value="outdoor">Outdoor Cricket</option>
                <option value="indoor">Indoor Cricket</option>
              </CFormSelect>
            </CCol>
          )}

          {!isMatchContest && (
            <CCol md={6}>
              <CFormLabel>Number of teams</CFormLabel>
              <CFormSelect
                value={plan.team_band}
                onChange={(e) => setField('team_band', e.target.value)}
                disabled={!plan.competition_category}
              >
                <option value="">Select team band</option>
                {teamBands.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </CFormSelect>
            </CCol>
          )}

          <CCol md={6}>
            <CFormLabel>Duration</CFormLabel>
            <CFormSelect
              value={plan.duration}
              onChange={(e) => setField('duration', e.target.value)}
            >
              {DURATION_OPTIONS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label} ({d.sublabel})
                </option>
              ))}
            </CFormSelect>
          </CCol>
        </CRow>

        {planChanged && (
          <div className="border rounded p-3 mt-3">
            <div className="small text-medium-emphasis mb-1">New plan</div>
            <div className="fw-semibold mb-2">{productName || '-'}</div>
            {pricing.finalPrice !== null ? (
              <div className="small">
                Base {formatAmount(pricing.basePrice)}
                {isClub && pricing.priceAdjustment !== 0 && (
                  <>
                    {' '}
                    · Duration {pricing.priceAdjustment > 0 ? '+' : '−'}
                    {formatAmount(Math.abs(pricing.priceAdjustment))}
                  </>
                )}{' '}
                · <strong>Plan price {formatAmount(pricing.finalPrice)}</strong>
              </div>
            ) : (
              <div className="small text-medium-emphasis">
                Select all options to see the plan price.
              </div>
            )}
          </div>
        )}

        {gameTypeChanges && (
          <CAlert color="warning" className="small mt-3 mb-0">
            This also changes the game from <strong>{GAME_TYPE_LABELS[item.game_type]}</strong> to{' '}
            <strong>{GAME_TYPE_LABELS[newGameType]}</strong>, which changes the menus the game admin
            sees.
          </CAlert>
        )}

        <CRow className="g-3 mt-1">
          <CCol md={6}>
            <CFormLabel>{isUpdate ? 'Amount paid' : 'Amount received'}</CFormLabel>
            <CInputGroup>
              <CInputGroupText>$</CInputGroupText>
              <CFormInput
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmountOverride(e.target.value)}
              />
            </CInputGroup>
            {amountOverride !== null ? (
              <CButton
                color="link"
                size="sm"
                className="p-0 mt-1"
                onClick={() => setAmountOverride(null)}
              >
                Use plan price
              </CButton>
            ) : (
              <div className="form-text">Follows the plan price. Type to override.</div>
            )}
          </CCol>
          {!isUpdate && (
            <CCol md={6}>
              <CFormLabel>Note (optional)</CFormLabel>
              <CFormTextarea
                rows={2}
                placeholder="e.g. Paid by bank transfer on 6 Oct"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </CCol>
          )}
        </CRow>

        <div className="small text-medium-emphasis mt-3">
          {isUpdate
            ? 'This updates the paid plan in place. The game stays paid and no new activation is recorded.'
            : 'This marks the plan as paid and records the activation. The game admin will then be able to activate their game.'}
        </div>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </CButton>
        {isUpdate ? (
          <CButton
            color="primary"
            onClick={handleSave}
            disabled={
              saving ||
              amount === '' ||
              (!planChanged && !amountChanged) ||
              (planChanged && !planComplete)
            }
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </CButton>
        ) : (
          <CButton
            color="success"
            onClick={handleSave}
            disabled={saving || amount === '' || (planChanged && !planComplete)}
          >
            {saving ? 'Saving...' : `Mark Paid${amount !== '' ? ` · ${formatAmount(amount)}` : ''}`}
          </CButton>
        )}
      </CModalFooter>
    </CModal>
  )
}

export default MarkPaidModal
